import React, { useEffect, useRef, useState } from 'react';
import './main.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchThemeData } from '../../admin/themeSlice';
import { selectAdminToken } from '../../admin/sessionSlice';
import { setBackButtonUrl, setMainDaySubViewOpen } from '../uiSlice';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import { fetchReport } from '../../functions/fetchReport';
import { initStageReport, updateStageProgress } from '../../functions/stageReport';
import { extractReportIdFromResponse, normalizeReportTimeValue, resolveReportsAuthToken, sendReport } from '../../functions/sendReport';
import { isUserDemoBypass } from '../../utils/demoGate';
import { mergeReportId } from '../loginSlice';
import Day1 from './days/Day1';
import Day2 from './days/Day2';
import Day3 from './days/Day3';
import Day4 from './days/Day4';
import Day5 from './days/Day5';
import Day6 from './days/Day6';
import Day7 from './days/Day7';

const DAY_COMPONENTS = {
  1: Day1,
  2: Day2,
  3: Day3,
  4: Day4,
  5: Day5,
  6: Day6,
  7: Day7,
};

function isDayUnlocked(dayToggleValue) {
  return (
    dayToggleValue === 'true' ||
    dayToggleValue === true ||
    dayToggleValue === 1 ||
    String(dayToggleValue ?? '').toLowerCase() === 'true'
  );
}

function allUnlockedStagesPlayed(stages, reportRow) {
  if (!Array.isArray(stages) || stages.length === 0 || !reportRow) return false;
  let anyUnlocked = false;
  for (let i = 0; i < stages.length; i++) {
    if (!isDayUnlocked(stages[i]?.day_toggle)) continue;
    anyUnlocked = true;
    const dayNumber = i + 1;
    const key = `day${dayNumber}_answer`;
    if (String(reportRow[key] ?? '').trim() === '') return false;
  }
  return anyUnlocked;
}

function parseAllCorrectFromDayAnswer(raw) {
  if (!raw) return false;
  const s = String(raw).trim();
  if (!s) return false;
  try {
    const obj = JSON.parse(s);
    return Boolean(obj?.allCorrect);
  } catch {
    return false;
  }
}

function normalizeReportRoleFromUser(user) {
  const src = String(user?.source ?? '').trim().toUpperCase();
  const r = String(user?.role ?? '').trim().toUpperCase();
  if (src === 'GUEST_USER' || r === 'GUEST_USER') return 'GUEST_USER';
  if (src === 'ORG_USER' && (r === 'PLAYER' || r === 'PLAYERS' || r === '')) return 'ORG_USER';
  return r || src || 'ORG_USER';
}

function readStoredReportId() {
  try {
    const raw = sessionStorage.getItem('userData');
    if (!raw) return null;
    const u = JSON.parse(raw);
    const rid = u?.reportId ?? u?.reportid ?? u?.report_id;
    const s = rid != null ? String(rid).trim() : '';
    return s ? s : null;
  } catch {
    return null;
  }
}

function MainPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeDay, setActiveDay] = useState(null);
  const mainGridBackNonce = useSelector((state) => state.ui.mainGridBackNonce);
  const prevGridBackNonce = useRef(mainGridBackNonce);
  const authUser = useSelector((state) => state.auth.user);

  const { data: themeData, status } = useSelector((state) => state.theme);
  const adminToken = useSelector(selectAdminToken);

  const [stages, setStages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stageImgFailed, setStageImgFailed] = useState({});
  const [reportData, setReportData] = useState(null);
  const [dayStartedAt, setDayStartedAt] = useState(null);
  const initialReportSyncedRef = useRef(false);
  const reportSyncInFlightRef = useRef(false);
  const lastReportSyncFingerprintRef = useRef(null);

  const syncReportToSendReport = async (row) => {
    if (!row || !authUser?.userId || !adminToken) return;
    const tokenForReports = resolveReportsAuthToken(authUser?.token, adminToken);
    if (!tokenForReports) return;
    const points = Math.max(0, Math.floor(Number(row?.total_score ?? row?.points ?? 0) || 0));
    const time = normalizeReportTimeValue(row?.time);
    // Avoid inserting duplicate 0-point rows on every main-page visit.
    if (points === 0 && time === 0) return;

    const fingerprint = `${points}|${time}`;
    if (lastReportSyncFingerprintRef.current === fingerprint) return;
    if (reportSyncInFlightRef.current) return;
    const reportIdRaw = String(row?.reportid ?? row?.reportId ?? authUser?.reportId ?? '').trim();
    const reportId = reportIdRaw !== '' ? reportIdRaw : readStoredReportId();
    const rawName = String(authUser?.name ?? '').trim();
    const safeName =
      rawName && rawName.toLowerCase() !== 'null null' && !rawName.toLowerCase().includes('null')
        ? rawName
        : String(authUser?.email ?? '').trim();
    const safeEmail = String(authUser?.email ?? '').trim();
    const reportRole = normalizeReportRoleFromUser(authUser);
    const isGuest = reportRole === 'GUEST_USER';
    try {
      reportSyncInFlightRef.current = true;
      const payload = {
        reportId,
        points,
        time,
        gameId: authUser?.gameId,
        name: safeName,
        email: safeEmail || null,
        sessionId: authUser?.sessionId,
        userId: authUser?.userId,
        organizationId: authUser?.organizationId,
        role: reportRole,
        user_type: reportRole,
        owner: reportRole,
        ans: '',
        guestScoreMinimal: isGuest,
        token: tokenForReports,
      };
      let res;
      try {
        res = await sendReport(payload);
      } catch (firstErr) {
        // If update fails due to stale reportId, retry insert.
        // If insert fails due to duplicate/unique constraint, retry update (sendReport will resolve reportId).
        if (payload.reportId) {
          res = await sendReport({ ...payload, reportId: null });
        } else {
          const msg = String(firstErr?.response?.data?.message || firstErr?.message || '');
          const prismaCode = firstErr?.response?.data?.err?.code;
          const looksLikeDuplicate =
            prismaCode === 'P2002' || /unique/i.test(msg) || /duplicate/i.test(msg) || /already exists/i.test(msg);
          if (looksLikeDuplicate) {
            res = await sendReport(payload);
          } else {
            throw firstErr;
          }
        }
      }

      const rid = extractReportIdFromResponse(res, authUser?.userId);
      if (rid && String(rid).trim() !== '') {
        const clean = String(rid).trim();
        dispatch(mergeReportId(clean));
        // Persist report id so next sends can update + backend stages gets reportid.
        try {
          const stored = sessionStorage.getItem('userData');
          if (stored) {
            const u = JSON.parse(stored);
            const next = { ...(u || {}), reportId: clean };
            sessionStorage.setItem('userData', JSON.stringify(next));
          }
        } catch {
          /* ignore */
        }
        try {
          await initStageReport({
            token: adminToken,
            userId: authUser?.userId,
            email: safeEmail,
            name: safeName,
            userType: reportRole,
            reportId: clean,
            themeName: themeData?.themename || 'Daily Brain Dose',
          });
        } catch {
          /* ignore */
        }
      }
      lastReportSyncFingerprintRef.current = fingerprint;
    } catch (e) {
      console.warn('sendReport failed:', e?.message || e);
    } finally {
      reportSyncInFlightRef.current = false;
    }
  };

  const loadReport = async () => {
    if (!authUser?.userId || !adminToken) return;
    try {
      const resp = await fetchReport({ userId: authUser.userId, token: adminToken });
      const row = resp?.report || null;
      setReportData(row);
      return row;
    } catch (e) {
      console.warn('fetchReport failed:', e?.message || e);
      return null;
    }
  };

  useEffect(() => {
    if (!adminToken) {
      setIsLoading(false);
      return;
    }
    if (status === 'idle') dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch, adminToken, status]);

  useEffect(() => {
    dispatch(setBackButtonUrl('/login?&save=true'));
  }, [dispatch]);

  useEffect(() => {
    dispatch(setMainDaySubViewOpen(!!activeDay));
    return () => dispatch(setMainDaySubViewOpen(false));
  }, [activeDay, dispatch]);

  useEffect(() => {
    if (activeDay != null && mainGridBackNonce !== prevGridBackNonce.current) {
      setActiveDay(null);
      setDayStartedAt(null);
    }
    prevGridBackNonce.current = mainGridBackNonce;
  }, [mainGridBackNonce, activeDay]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, authUser?.userId]);

  useEffect(() => {
    if (initialReportSyncedRef.current) return;
    if (!reportData) return;
    initialReportSyncedRef.current = true;
    void syncReportToSendReport(reportData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData]);

  useEffect(() => {
    if (themeData && themeData.stages) {
      setStages(Array.isArray(themeData.stages) ? themeData.stages : []);
      setIsLoading(false);
    } else if (status === 'failed') {
      setIsLoading(false);
    } else if (status === 'succeeded' && (!themeData || !themeData.stages)) {
      setStages([]);
      setIsLoading(false);
    } else if (status === 'idle') {
      setIsLoading(true);
    }
  }, [status, themeData]);

  useEffect(() => {
    if (isLoading || activeDay) return;
    if (!allUnlockedStagesPlayed(stages, reportData)) return;
    navigate('/leaderboard', { replace: true });
  }, [isLoading, activeDay, stages, reportData, navigate]);

  const handleStageClick = (index) => {
    const stage = stages[index];
    if (!stage) return;
    if (!isDayUnlocked(stage.day_toggle)) return;
    const dayNum = index + 1;
    const dayAnswerKey = `day${dayNum}_answer`;
    const alreadyPlayed = String(reportData?.[dayAnswerKey] ?? '').trim() !== '';
    if (alreadyPlayed) return;
    if (dayNum >= 1 && dayNum <= 7 && DAY_COMPONENTS[dayNum]) {
      setActiveDay(dayNum);
      setDayStartedAt(Date.now());
    }
  };

  const handleStageComplete = async ({
    dayNumber,
    earnedPoints = 0,
    answer = '',
    allCorrect = false,
  }) => {
    if (!authUser?.userId || !adminToken || !dayNumber) {
      setActiveDay(null);
      setDayStartedAt(null);
      return;
    }
    const elapsedSeconds = Math.max(
      0,
      Math.floor((Date.now() - Number(dayStartedAt || Date.now())) / 1000)
    );
    try {
      const completionBonus = Math.max(
        0,
        Math.floor(Number(themeData?.completition_bonus_points ?? 0) || 0)
      );
      const perfectBonus = Math.max(
        0,
        Math.floor(Number(themeData?.perfect_score_bonus_points ?? 0) || 0)
      );

      const bonusToasts = [];
      const willBePlayed = (d) => {
        const key = `day${d}_answer`;
        if (d === Number(dayNumber)) return true;
        return String(reportData?.[key] ?? '').trim() !== '';
      };
      const allDaysPlayedAfterThis = [1, 2, 3, 4, 5, 6, 7].every(willBePlayed);

      const willBeAllCorrect = (d) => {
        if (d === Number(dayNumber)) return Boolean(allCorrect);
        const key = `day${d}_answer`;
        return parseAllCorrectFromDayAnswer(reportData?.[key]);
      };
      const allDaysAllCorrectAfterThis =
        allDaysPlayedAfterThis && [1, 2, 3, 4, 5, 6, 7].every(willBeAllCorrect);

      const completionAward = allDaysPlayedAfterThis ? completionBonus : 0;
      const perfectAward = allDaysAllCorrectAfterThis ? perfectBonus : 0;
      const totalPointsToSave =
        Math.max(0, Math.floor(Number(earnedPoints) || 0)) + completionAward + perfectAward;

      if (completionAward > 0) bonusToasts.push({ label: 'Completion bonus', points: completionAward });
      if (perfectAward > 0) bonusToasts.push({ label: 'Perfect score bonus', points: perfectAward });

      const resp = await updateStageProgress({
        token: adminToken,
        userId: authUser.userId,
        dayNumber,
        points: totalPointsToSave,
        timeSeconds: elapsedSeconds,
        answer,
        allCorrect,
        email: String(authUser?.email ?? '').trim(),
        name: String(authUser?.name ?? '').trim() || String(authUser?.email ?? '').trim(),
      });
      const row = await loadReport();
      if (row) await syncReportToSendReport(row);
      const stageId =
        resp?.report?.id != null && Number.isFinite(Number(resp.report.id))
          ? Number(resp.report.id)
          : null;
      navigate('/streak', { replace: true, state: { dayNumber, stageId, bonusToasts } });
    } catch (e) {
      console.warn('updateStageProgress failed:', e?.message || e);
    } finally {
      setActiveDay(null);
      setDayStartedAt(null);
    }
  };

  if (isLoading) {
    return (
      <div className="main-loader-container">
        <div className="main-loader" />
      </div>
    );
  }

  if (activeDay) {
    const DayView = DAY_COMPONENTS[activeDay];
    const stageForDay = stages[activeDay - 1];
    if (DayView) {
      return (
        <div className="main-page-container main-page-container--day-full">
          <DayView
            stage={stageForDay}
            onStageComplete={(payload = {}) =>
              handleStageComplete({
                ...payload,
                dayNumber: activeDay,
              })
            }
          />
        </div>
      );
    }
  }

  // Render a single stage card
  const renderStageCard = (stage, index) => {
    const dayNumber = index + 1;
    const stageKey = `stage_${dayNumber}`;
    const stageData = themeData?.stages_data?.[stageKey] || {};
    const dayAnswerKey = `day${dayNumber}_answer`;
    const alreadyPlayed = String(reportData?.[dayAnswerKey] ?? '').trim() !== '';

    const badgeImage = stageData.badge || stage.image;
    const stageImage = s3PublicUrl(badgeImage);

    const isDemo = isUserDemoBypass(authUser);
    const isUnlocked = isDemo ? true : isDayUnlocked(stage.day_toggle);
    const canPlay = isDemo ? true : (isUnlocked && !alreadyPlayed);
    const lockOnUrl = s3PublicUrl(themeData?.lock_image);
    const lockOffUrl = s3PublicUrl(themeData?.unlocked_image);
    const lockIconUrl = isUnlocked ? lockOffUrl : lockOnUrl;

    return (
      <div
        key={`stage-${index}`}
        className={`main-stage-card ${canPlay ? 'unlocked' : 'locked'}`}
        onClick={() => handleStageClick(index)}
        role="button"
        tabIndex={canPlay ? 0 : -1}
        onKeyDown={(e) => {
          if (canPlay && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleStageClick(index);
          }
        }}
      >
        <div className="main-stage-image-wrapper">
          {stageImage && !stageImgFailed[index] ? (
            <img
              src={stageImage}
              alt={stage.name || `Day ${dayNumber}`}
              className="main-stage-image"
              onError={() =>
                setStageImgFailed((prev) => ({ ...prev, [index]: true }))
              }
            />
          ) : (
            <div
              className="main-stage-image-fallback"
              aria-hidden
              title={stage.name || `Day ${dayNumber}`}
            >
              {dayNumber}
            </div>
          )}
          {lockIconUrl ? (
            <img
              src={lockIconUrl}
              alt=""
              className="main-stage-lock-icon"
              aria-hidden
            />
          ) : null}
        </div>
      </div>
    );
  };

  // Split into rows: first 4, then remaining (up to 3)
  const firstRow = stages.slice(0, 4);
  const secondRow = stages.slice(4);

  return (
    <div className="main-page-container">
      <div className="main-stages-grid">
        {/* Row 1: Days 1–4 */}
        <div className="main-stages-row">
          {firstRow.map((stage, index) => renderStageCard(stage, index))}
        </div>

        {/* Row 2: Days 5–7, centered (same card width as row 1) */}
        {secondRow.length > 0 && (
          <div className="main-stages-row main-stages-row--three">
            {secondRow.map((stage, index) => renderStageCard(stage, index + 4))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MainPage;
